import { supabase } from '../lib/supabase';

export const auditTrailService = {
  // ============================================================================
  // COMPREHENSIVE AUDIT TRAIL AGGREGATION
  // ============================================================================

  async getAllAuditEntries(filters = {}) {
    try {
      const {
        entityType,
        actionType,
        performedBy,
        dateFrom,
        dateTo,
        searchTerm,
        limit = 100,
        offset = 0
      } = filters;

      // Fetch from all three audit tables
      const [auditLogs, reportAudit, approvalAudit] = await Promise.all([
        this.getAuditLogs(filters),
        this.getReportAuditTrail(filters),
        this.getApprovalHistory(filters)
      ]);

      // Combine and normalize all audit entries
      const allEntries = [
        ...auditLogs?.map(entry => ({
          id: entry?.id,
          source: 'system',
          entityType: entry?.entityType,
          entityId: entry?.entityId,
          actionType: entry?.action,
          actionDescription: `${entry?.action} on ${entry?.entityType}`,
          performedBy: entry?.performedBy,
          performedAt: entry?.createdAt,
          changes: entry?.changes,
          metadata: null
        })),
        ...reportAudit?.map(entry => ({
          id: entry?.id,
          source: 'compliance_report',
          entityType: entry?.entityType || 'compliance_report',
          entityId: entry?.entityId || entry?.reportId,
          actionType: entry?.actionType,
          actionDescription: entry?.actionDescription,
          performedBy: entry?.performedBy,
          performedAt: entry?.performedAt,
          changes: entry?.changesMade,
          metadata: entry?.metadata
        })),
        ...approvalAudit?.map(entry => ({
          id: entry?.id,
          source: 'approval_workflow',
          entityType: 'approval',
          entityId: entry?.approvalInstanceId,
          actionType: entry?.actionType,
          actionDescription: entry?.actionDescription,
          performedBy: entry?.performedBy,
          performedAt: entry?.createdAt,
          changes: { previousStatus: entry?.previousStatus, newStatus: entry?.newStatus },
          metadata: entry?.metadata
        }))
      ];

      // Sort by date descending
      allEntries?.sort((a, b) => new Date(b.performedAt) - new Date(a.performedAt));

      // Apply search filter if provided
      let filteredEntries = allEntries;
      if (searchTerm) {
        const lowerSearch = searchTerm?.toLowerCase();
        filteredEntries = allEntries?.filter(entry =>
          entry?.actionDescription?.toLowerCase()?.includes(lowerSearch) ||
          entry?.performedBy?.toLowerCase()?.includes(lowerSearch) ||
          entry?.entityType?.toLowerCase()?.includes(lowerSearch)
        );
      }

      // Apply pagination
      const paginatedEntries = filteredEntries?.slice(offset, offset + limit);

      return {
        entries: paginatedEntries,
        total: filteredEntries?.length,
        hasMore: filteredEntries?.length > offset + limit
      };
    } catch (error) {
      console.error('Error fetching audit entries:', error);
      throw error;
    }
  },

  async getAuditLogs(filters = {}) {
    let query = supabase?.from('audit_logs')?.select('*');

    if (filters?.entityType) {
      query = query?.eq('entity_type', filters?.entityType);
    }

    if (filters?.actionType) {
      query = query?.eq('action', filters?.actionType);
    }

    if (filters?.performedBy) {
      query = query?.ilike('performed_by', `%${filters?.performedBy}%`);
    }

    if (filters?.dateFrom) {
      query = query?.gte('created_at', filters?.dateFrom);
    }

    if (filters?.dateTo) {
      query = query?.lte('created_at', filters?.dateTo);
    }

    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      entityType: row?.entity_type,
      entityId: row?.entity_id,
      action: row?.action,
      performedBy: row?.performed_by,
      changes: row?.changes,
      createdAt: row?.created_at
    })) || [];
  },

  async getReportAuditTrail(filters = {}) {
    let query = supabase?.from('report_audit_trail')?.select('*');

    if (filters?.entityType) {
      query = query?.eq('entity_type', filters?.entityType);
    }

    if (filters?.actionType) {
      query = query?.eq('action_type', filters?.actionType);
    }

    if (filters?.performedBy) {
      query = query?.ilike('performed_by', `%${filters?.performedBy}%`);
    }

    if (filters?.dateFrom) {
      query = query?.gte('performed_at', filters?.dateFrom);
    }

    if (filters?.dateTo) {
      query = query?.lte('performed_at', filters?.dateTo);
    }

    query = query?.order('performed_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      reportId: row?.report_id,
      actionType: row?.action_type,
      actionDescription: row?.action_description,
      performedBy: row?.performed_by,
      performedAt: row?.performed_at,
      entityType: row?.entity_type,
      entityId: row?.entity_id,
      changesMade: row?.changes_made,
      metadata: row?.metadata
    })) || [];
  },

  async getApprovalHistory(filters = {}) {
    let query = supabase?.from('approval_history')?.select('*');

    if (filters?.actionType) {
      query = query?.eq('action_type', filters?.actionType);
    }

    if (filters?.performedBy) {
      query = query?.ilike('performed_by', `%${filters?.performedBy}%`);
    }

    if (filters?.dateFrom) {
      query = query?.gte('created_at', filters?.dateFrom);
    }

    if (filters?.dateTo) {
      query = query?.lte('created_at', filters?.dateTo);
    }

    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      approvalInstanceId: row?.approval_instance_id,
      actionType: row?.action_type,
      actionDescription: row?.action_description,
      performedBy: row?.performed_by,
      stepOrder: row?.step_order,
      previousStatus: row?.previous_status,
      newStatus: row?.new_status,
      metadata: row?.metadata,
      createdAt: row?.created_at
    })) || [];
  },

  // ============================================================================
  // STATISTICS & ANALYTICS
  // ============================================================================

  async getAuditStatistics(dateFrom, dateTo) {
    try {
      const filters = { dateFrom, dateTo };
      const { entries } = await this.getAllAuditEntries(filters);

      const stats = {
        totalActions: entries?.length,
        bySource: {},
        byActionType: {},
        byEntityType: {},
        topPerformers: {},
        recentActivity: entries?.slice(0, 10)
      };

      entries?.forEach(entry => {
        // Count by source
        stats.bySource[entry.source] = (stats?.bySource?.[entry?.source] || 0) + 1;

        // Count by action type
        stats.byActionType[entry.actionType] = (stats?.byActionType?.[entry?.actionType] || 0) + 1;

        // Count by entity type
        stats.byEntityType[entry.entityType] = (stats?.byEntityType?.[entry?.entityType] || 0) + 1;

        // Count by performer
        stats.topPerformers[entry.performedBy] = (stats?.topPerformers?.[entry?.performedBy] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error fetching audit statistics:', error);
      throw error;
    }
  },

  // ============================================================================
  // EXPORT FUNCTIONALITY
  // ============================================================================

  async exportAuditTrail(filters = {}, format = 'csv') {
    try {
      const { entries } = await this.getAllAuditEntries({ ...filters, limit: 10000 });

      if (format === 'csv') {
        return this.generateCSV(entries);
      } else if (format === 'json') {
        return JSON.stringify(entries, null, 2);
      }

      throw new Error(`Unsupported export format: ${format}`);
    } catch (error) {
      console.error('Error exporting audit trail:', error);
      throw error;
    }
  },

  generateCSV(entries) {
    const headers = [
      'ID',
      'Source',
      'Entity Type',
      'Entity ID',
      'Action Type',
      'Action Description',
      'Performed By',
      'Performed At',
      'Changes'
    ];

    const rows = entries?.map(entry => [
      entry?.id,
      entry?.source,
      entry?.entityType,
      entry?.entityId,
      entry?.actionType,
      entry?.actionDescription,
      entry?.performedBy,
      new Date(entry.performedAt)?.toLocaleString('en-PG'),
      JSON.stringify(entry?.changes || {})
    ]);

    const csvContent = [
      headers?.join(','),
      ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
    ]?.join('\n');

    return csvContent;
  },

  downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  },

  // ============================================================================
  // FILTER OPTIONS
  // ============================================================================

  async getFilterOptions() {
    try {
      const [auditLogs, reportAudit, approvalAudit] = await Promise.all([
        supabase?.from('audit_logs')?.select('entity_type, action, performed_by'),
        supabase?.from('report_audit_trail')?.select('entity_type, action_type, performed_by'),
        supabase?.from('approval_history')?.select('action_type, performed_by')
      ]);

      const entityTypes = new Set();
      const actionTypes = new Set();
      const performers = new Set();

      // Collect unique values from audit_logs
      auditLogs?.data?.forEach(row => {
        if (row?.entity_type) entityTypes?.add(row?.entity_type);
        if (row?.action) actionTypes?.add(row?.action);
        if (row?.performed_by) performers?.add(row?.performed_by);
      });

      // Collect unique values from report_audit_trail
      reportAudit?.data?.forEach(row => {
        if (row?.entity_type) entityTypes?.add(row?.entity_type);
        if (row?.action_type) actionTypes?.add(row?.action_type);
        if (row?.performed_by) performers?.add(row?.performed_by);
      });

      // Collect unique values from approval_history
      approvalAudit?.data?.forEach(row => {
        if (row?.action_type) actionTypes?.add(row?.action_type);
        if (row?.performed_by) performers?.add(row?.performed_by);
      });

      return {
        entityTypes: Array.from(entityTypes)?.sort(),
        actionTypes: Array.from(actionTypes)?.sort(),
        performers: Array.from(performers)?.sort()
      };
    } catch (error) {
      console.error('Error fetching filter options:', error);
      throw error;
    }
  }
};

export default auditTrailService;